/**
 * App-logic TypeScript pipeline (ADR-0054 S2) — the PURE, save-time compiler the
 * Query editor runs for a `js` (Compute) / `logic` body authored in TypeScript.
 *
 * The keystone (why TS-over-QuickJS was chosen): TypeScript is a TYPING LAYER at
 * authoring time, ERASED at runtime. The QuickJS runtime is UNCHANGED — it still
 * evals a raw function body with a global `ctx` (`(function(ctx){ …body… })(ctx)`).
 * So this module does two erasure-time jobs, no runtime involvement:
 *
 *  1. **Transpile** the TS source → plain JS (`code_js`, what QuickJS runs). For a
 *     plain-JS body (no types) this is an identity passthrough → back-compat: an
 *     existing untyped body keeps running verbatim.
 *  2. **Extract the I/O contract** from the OPTIONAL `interface Input` / `interface
 *     Output` (or `type` alias) the operator declares at the top of the body —
 *     WITHOUT running the code. `Input` seeds the declared `input_schema` (ADR-0054
 *     S1); `Output` gives the statically-known `output_schema` (REST response +
 *     `$queries.<key>` intellisense) — the whole point of the ADR.
 *
 * The extractor is a pure AST walk (`ts.createSourceFile`, no Program, no lib files
 * → fast + dependency-light), bounded to the LOW-CODE SUBSET the ADR scopes:
 * primitives, arrays, nested object literals/interfaces, optionals, and
 * unions-of-literals. Anything exotic degrades to a permissive schema, never a
 * wrong one. SEMANTIC type-checking (catching a `ctx.db.query` typo against the
 * ambient DTS) is Monaco's job in the live editor (it has the libs + DTS loaded);
 * this module reports only the SYNTACTIC diagnostics `transpileModule` surfaces.
 *
 * `typescript` (already a dependency) is lazy-imported so it loads ONLY when the
 * Query editor actually compiles a body — never on the general app boot path.
 */
import type * as TS from "typescript";

import type { QueryParam, QueryParamType } from "./api-apps";

/** Cheap syntactic guess (no `typescript` load) whether a body uses the `handler`
 *  function shape — drives the editor's Monaco language (TS vs lenient JS). A raw
 *  legacy body has top-level `return`, which full TS mode would wrongly flag, so it
 *  stays in JS mode. */
export function looksLikeHandler(src: string): boolean {
  return /(^|\n|;)\s*(export\s+)?(async\s+)?function\s+handler\b/.test(src) ||
    /(^|\n|;)\s*(export\s+)?(const|let|var)\s+handler\s*=/.test(src);
}

let _tsPromise: Promise<typeof TS> | null = null;
/** Lazy-load `typescript` once (kept off the boot path — only the Query editor pays). */
function loadTs(): Promise<typeof TS> {
  if (!_tsPromise) _tsPromise = import("typescript");
  return _tsPromise;
}

/** A single-property JSON-schema-ish node (the bounded subset we extract/emit). */
export interface JsonSchemaNode {
  type?: "string" | "number" | "boolean" | "object" | "array" | "null";
  properties?: Record<string, JsonSchemaNode>;
  required?: string[];
  items?: JsonSchemaNode;
  enum?: (string | number | boolean)[];
}

export interface LogicDiagnostic {
  message: string;
  /** 1-based source line, or null when the diagnostic has no position. */
  line: number | null;
}

export interface LogicContract {
  /** Declared inputs (from `interface Input`) as contract params, or null when the
   *  body declares no `Input` type (→ keep the manually-authored contract). */
  input: QueryParam[] | null;
  /** Declared output shape (from `interface Output`), or null when absent. */
  output: JsonSchemaNode | null;
}

export interface LogicCompileResult {
  /** No SYNTACTIC error — safe to persist. (Semantic type errors come from Monaco.) */
  ok: boolean;
  /** The transpiled JS body QuickJS runs (types erased). For a `handler(ctx,input)`
   *  source this is the transpiled function PLUS a `return handler(ctx, ctx.input);`
   *  bridge, so the UNCHANGED raw-body runtime runs it as-is. */
  codeJs: string;
  /** The v3 source map (JSON string) from TS→JS, with `sourcesContent` inlined so it's
   *  self-contained. Persisted as `spec.code_map` and used by the debugger to step the
   *  TS source while QuickJS runs the JS (ADR-0056 TS-debug). null on a compile failure. */
  sourceMap: string | null;
  diagnostics: LogicDiagnostic[];
  contract: LogicContract;
  /** True when the source is the `handler(ctx, input): Output` shape (vs a raw body). */
  hasHandler: boolean;
}

// -- type-node → schema (pure AST, bounded subset) ---------------------------

/** Map a schema node's coarse `type` to a contract `QueryParamType`. */
function schemaToParamType(node: JsonSchemaNode): QueryParamType {
  switch (node.type) {
    case "string":
    case "number":
    case "boolean":
    case "array":
    case "object":
      return node.type;
    default:
      return "object"; // `any`/unknown → the most permissive structured type
  }
}

/** Build a schema from a TS TypeNode, resolving locally-declared interface/type
 *  references by name (with a cycle guard). Unknown constructs → `{}` (permissive). */
function typeNodeToSchema(
  ts: typeof TS,
  node: TS.TypeNode | undefined,
  decls: Map<string, TS.TypeNode | TS.InterfaceDeclaration>,
  seen: Set<string>,
): JsonSchemaNode {
  if (!node) return {};
  const K = ts.SyntaxKind;
  switch (node.kind) {
    case K.StringKeyword:
      return { type: "string" };
    case K.NumberKeyword:
      return { type: "number" };
    case K.BooleanKeyword:
      return { type: "boolean" };
    case K.ArrayType:
      return {
        type: "array",
        items: typeNodeToSchema(ts, (node as TS.ArrayTypeNode).elementType, decls, seen),
      };
    case K.TypeLiteral:
      return objectSchema(ts, (node as TS.TypeLiteralNode).members, decls, seen);
    case K.ParenthesizedType:
      return typeNodeToSchema(ts, (node as TS.ParenthesizedTypeNode).type, decls, seen);
    case K.LiteralType: {
      const lit = (node as TS.LiteralTypeNode).literal;
      if (ts.isStringLiteral(lit)) return { type: "string", enum: [lit.text] };
      if (ts.isNumericLiteral(lit)) return { type: "number", enum: [Number(lit.text)] };
      if (lit.kind === K.TrueKeyword) return { type: "boolean", enum: [true] };
      if (lit.kind === K.FalseKeyword) return { type: "boolean", enum: [false] };
      return {};
    }
    case K.UnionType:
      return unionSchema(ts, node as TS.UnionTypeNode, decls, seen);
    case K.TypeReference: {
      const ref = node as TS.TypeReferenceNode;
      const name = ts.isIdentifier(ref.typeName) ? ref.typeName.text : ref.typeName.getText();
      // Array<T> / ReadonlyArray<T>
      if ((name === "Array" || name === "ReadonlyArray") && ref.typeArguments?.length) {
        return { type: "array", items: typeNodeToSchema(ts, ref.typeArguments[0], decls, seen) };
      }
      // A locally-declared interface/type → resolve by name (cycle-guarded).
      const target = decls.get(name);
      if (target && !seen.has(name)) {
        const next = new Set(seen).add(name);
        if (ts.isInterfaceDeclaration(target)) return objectSchema(ts, target.members, decls, next);
        return typeNodeToSchema(ts, target as TS.TypeNode, decls, next);
      }
      return {}; // Record/Partial/imported/unknown → permissive
    }
    default:
      return {};
  }
}

function unionSchema(
  ts: typeof TS,
  node: TS.UnionTypeNode,
  decls: Map<string, TS.TypeNode | TS.InterfaceDeclaration>,
  seen: Set<string>,
): JsonSchemaNode {
  // Drop `undefined`/`null` members (they signal optionality, handled at the property).
  const K = ts.SyntaxKind;
  const members = node.types.filter(
    (t) =>
      t.kind !== K.UndefinedKeyword &&
      !(t.kind === K.LiteralType && (t as TS.LiteralTypeNode).literal.kind === K.NullKeyword),
  );
  const subs = members.map((t) => typeNodeToSchema(ts, t, decls, seen));
  if (subs.length === 1) return subs[0];
  // A union of same-typed literals → an enum (the low-code "select" case).
  const enums = subs.filter((s) => s.enum && s.enum.length === 1);
  if (enums.length === subs.length && enums.length > 0) {
    const t = enums[0].type;
    if (enums.every((s) => s.type === t)) {
      return { type: t, enum: enums.flatMap((s) => s.enum!) };
    }
  }
  return {}; // heterogeneous union → permissive
}

function objectSchema(
  ts: typeof TS,
  members: TS.NodeArray<TS.TypeElement>,
  decls: Map<string, TS.TypeNode | TS.InterfaceDeclaration>,
  seen: Set<string>,
): JsonSchemaNode {
  const properties: Record<string, JsonSchemaNode> = {};
  const required: string[] = [];
  for (const m of members) {
    if (!ts.isPropertySignature(m) || !m.name) continue;
    const name = ts.isIdentifier(m.name) || ts.isStringLiteral(m.name) ? m.name.text : m.name.getText();
    properties[name] = typeNodeToSchema(ts, m.type, decls, seen);
    if (!m.questionToken) required.push(name);
  }
  const out: JsonSchemaNode = { type: "object", properties };
  if (required.length) out.required = required;
  return out;
}

/** Collect every top-level `interface`/`type` declaration so references resolve. */
function collectDeclarations(
  ts: typeof TS,
  sf: TS.SourceFile,
): Map<string, TS.TypeNode | TS.InterfaceDeclaration> {
  const decls = new Map<string, TS.TypeNode | TS.InterfaceDeclaration>();
  for (const st of sf.statements) {
    if (ts.isInterfaceDeclaration(st)) decls.set(st.name.text, st);
    else if (ts.isTypeAliasDeclaration(st)) decls.set(st.name.text, st.type);
  }
  return decls;
}

/** Find the `handler(ctx, input): Output` function (ADR-0054 canonical shape) — a
 *  `function handler(){}` OR a `const handler = (…) => …` / `= function(){}`. Returns
 *  the function-like node whose 2nd param type is `Input` and return type is `Output`,
 *  or null (→ the operator wrote a raw body; extract top-level `Input`/`Output` instead). */
function findHandler(
  ts: typeof TS,
  sf: TS.SourceFile,
): TS.FunctionDeclaration | TS.FunctionExpression | TS.ArrowFunction | null {
  for (const st of sf.statements) {
    if (ts.isFunctionDeclaration(st) && st.name?.text === "handler") return st;
    if (ts.isVariableStatement(st)) {
      for (const d of st.declarationList.declarations) {
        if (ts.isIdentifier(d.name) && d.name.text === "handler" && d.initializer) {
          if (ts.isArrowFunction(d.initializer) || ts.isFunctionExpression(d.initializer)) {
            return d.initializer;
          }
        }
      }
    }
  }
  return null;
}

function paramsFromObjectSchema(schema: JsonSchemaNode): QueryParam[] | null {
  if (schema.type !== "object" || !schema.properties) return null;
  const req = new Set(schema.required ?? []);
  return Object.entries(schema.properties).map(([name, s]) => ({
    name,
    type: schemaToParamType(s),
    required: req.has(name),
    // TS types can't express a REST location — default to body; the operator can
    // still adjust it in the Contratto editor (path/query for a REST endpoint).
    location: "body" as const,
  }));
}

/** Extract the `Input`/`Output` contract (pure AST, no run). Prefers the `handler`
 *  signature (2nd param = Input, return type = Output); falls back to top-level
 *  `interface Input`/`Output` for a raw body (back-compat). */
function extractContract(
  ts: typeof TS,
  decls: Map<string, TS.TypeNode | TS.InterfaceDeclaration>,
  handler: TS.FunctionDeclaration | TS.FunctionExpression | TS.ArrowFunction | null,
): LogicContract {
  const out: LogicContract = { input: null, output: null };
  // The handler's return/param types are usually a TypeReference (`Output`/`Input`);
  // start with an EMPTY seen set so that reference resolves once (the guard only stops
  // INFINITE self-reference during resolution).
  const schemaOf = (node: TS.TypeNode | undefined): JsonSchemaNode | null =>
    node ? typeNodeToSchema(ts, node, decls, new Set()) : null;

  if (handler) {
    out.output = schemaOf(handler.type);
    const inputSchema = schemaOf(handler.parameters[1]?.type);
    if (inputSchema) out.input = paramsFromObjectSchema(inputSchema);
    return out;
  }

  const outputDecl = decls.get("Output");
  if (outputDecl) {
    out.output = ts.isInterfaceDeclaration(outputDecl)
      ? objectSchema(ts, outputDecl.members, decls, new Set(["Output"]))
      : typeNodeToSchema(ts, outputDecl as TS.TypeNode, decls, new Set(["Output"]));
  }
  const inputDecl = decls.get("Input");
  if (inputDecl) {
    const schema = ts.isInterfaceDeclaration(inputDecl)
      ? objectSchema(ts, inputDecl.members, decls, new Set(["Input"]))
      : typeNodeToSchema(ts, inputDecl as TS.TypeNode, decls, new Set(["Input"]));
    out.input = paramsFromObjectSchema(schema);
  }
  return out;
}

// -- predicate lambda → filter tree (ADR-0055 S1b) ---------------------------
//
// The .NET `Expression<Func<T,bool>>` trick, done at SAVE TIME: a
// `ctx.db.query('deals', d => d.stage === 'won' && d.amount >= input.min)` is
// REWRITTEN (source→source) to `ctx.db.query('deals', { filter: <tree> })` — the
// lambda is NEVER executed. Value-side sub-expressions (`'won'`, `input.min`) are
// emitted VERBATIM as live JS in the object literal, so QuickJS evaluates them at
// runtime (no bound-param indirection). A column-side non-SQL construct
// (`someHelper(d.x)`, `d.a * 2 > d.b`) is NOT translatable → a save-time error.

/** A translation failure carrying the offending node (for a line number). */
class TranslateError extends Error {
  node: TS.Node;
  constructor(message: string, node: TS.Node) {
    super(message);
    this.node = node;
  }
}

const _CMP_OP: Record<number, string> = {}; // filled lazily from ts.SyntaxKind
const _FLIP: Record<string, string> = { gt: "lt", gte: "lte", lt: "gt", lte: "gte", eq: "eq", ne: "ne" };

/** The dotted column path if `node` is `<param>.a.b…` (a member chain rooted at the
 *  lambda param) — e.g. `d.company.name` → "company.name"; else null. */
function columnPath(ts: typeof TS, node: TS.Expression, param: string): string | null {
  const parts: string[] = [];
  let cur: TS.Expression = node;
  while (ts.isPropertyAccessExpression(cur)) {
    parts.unshift(cur.name.text);
    cur = cur.expression;
  }
  if (ts.isIdentifier(cur) && cur.text === param && parts.length) return parts.join(".");
  return null;
}

/** True if the subtree references the lambda param anywhere (→ a value side that
 *  touches the row is a column computation we can't push to SQL). */
function refsParam(ts: typeof TS, node: TS.Node, param: string): boolean {
  let found = false;
  const walk = (n: TS.Node): void => {
    if (found) return;
    if (ts.isIdentifier(n) && n.text === param) found = true;
    else ts.forEachChild(n, walk);
  };
  walk(node);
  return found;
}

function isNullLiteral(ts: typeof TS, node: TS.Expression): boolean {
  return node.kind === ts.SyntaxKind.NullKeyword;
}

/** Flatten a left-assoc chain of the SAME binary token (`a && b && c` → [a,b,c]). */
function flatten(ts: typeof TS, node: TS.Expression, tok: TS.SyntaxKind): TS.Expression[] {
  const out: TS.Expression[] = [];
  const rec = (n: TS.Expression): void => {
    const u = ts.isParenthesizedExpression(n) ? n.expression : n;
    if (ts.isBinaryExpression(u) && u.operatorToken.kind === tok) {
      rec(u.left);
      rec(u.right);
    } else out.push(u);
  };
  rec(node);
  return out;
}

/** Translate one boolean expression → a filter-tree object-literal SOURCE string. */
function translateExpr(ts: typeof TS, node: TS.Expression, param: string, sf: TS.SourceFile): string {
  const K = ts.SyntaxKind;
  const e = ts.isParenthesizedExpression(node) ? node.expression : node;

  if (ts.isPrefixUnaryExpression(e) && e.operator === K.ExclamationToken) {
    return `{ not: ${translateExpr(ts, e.operand, param, sf)} }`;
  }

  if (ts.isBinaryExpression(e)) {
    const op = e.operatorToken.kind;
    if (op === K.AmpersandAmpersandToken || op === K.BarBarToken) {
      const key = op === K.AmpersandAmpersandToken ? "and" : "or";
      const parts = flatten(ts, e, op).map((p) => translateExpr(ts, p, param, sf));
      return `{ ${key}: [${parts.join(", ")}] }`;
    }
    return translateComparison(ts, e, param, sf);
  }

  // `arrayLiteral.includes(d.col)` → an `in` leaf (unambiguous: array value on the left).
  if (ts.isCallExpression(e) && ts.isPropertyAccessExpression(e.expression) && e.expression.name.text === "includes") {
    const recv = e.expression.expression; // the array/value the .includes runs on
    const arg = e.arguments[0];
    const col = arg ? columnPath(ts, arg, param) : null;
    if (col && !refsParam(ts, recv, param)) {
      return `{ column: "${col}", op: "in", value: ${recv.getText(sf)} }`;
    }
    throw new TranslateError(
      `'${e.getText(sf)}' non è traducibile — usa \`array.includes(d.colonna)\` (op 'in'), ` +
        `oppure il filtro strutturato per contains/has.`,
      e,
    );
  }

  throw new TranslateError(
    `'${e.getText(sf)}' non è traducibile in un filtro server — usa confronti ` +
      `(=== > >= < <= !==), &&/||/!, o il filtro strutturato.`,
    e,
  );
}

function translateComparison(ts: typeof TS, e: TS.BinaryExpression, param: string, sf: TS.SourceFile): string {
  if (!Object.keys(_CMP_OP).length) {
    const K = ts.SyntaxKind;
    _CMP_OP[K.EqualsEqualsEqualsToken] = "eq";
    _CMP_OP[K.EqualsEqualsToken] = "eq";
    _CMP_OP[K.ExclamationEqualsEqualsToken] = "ne";
    _CMP_OP[K.ExclamationEqualsToken] = "ne";
    _CMP_OP[K.GreaterThanToken] = "gt";
    _CMP_OP[K.GreaterThanEqualsToken] = "gte";
    _CMP_OP[K.LessThanToken] = "lt";
    _CMP_OP[K.LessThanEqualsToken] = "lte";
  }
  let op = _CMP_OP[e.operatorToken.kind];
  if (!op) throw new TranslateError(`operatore '${e.operatorToken.getText(sf)}' non supportato nel filtro`, e);

  // Which side is the COLUMN (d.path) and which the VALUE?
  let col = columnPath(ts, e.left, param);
  let valNode: TS.Expression = e.right;
  if (!col) {
    col = columnPath(ts, e.right, param);
    valNode = e.left;
    op = _FLIP[op]; // `10 < d.x` ≡ `d.x > 10`
  }
  if (!col) {
    throw new TranslateError(
      `'${e.getText(sf)}': nessun lato è una colonna della riga (d.colonna) — ` +
        `una funzione/aritmetica sulla colonna non è spingibile in SQL.`,
      e,
    );
  }
  // `d.col == null` → is_null / not_null
  if (isNullLiteral(ts, valNode)) {
    if (op !== "eq" && op !== "ne") throw new TranslateError(`solo == / != con null`, e);
    return `{ column: "${col}", op: "${op === "eq" ? "is_null" : "not_null"}" }`;
  }
  // The value side must be a plain value — not another column reference.
  if (refsParam(ts, valNode, param)) {
    throw new TranslateError(
      `'${e.getText(sf)}': confronto colonna-vs-colonna non ancora supportato nel predicato.`,
      e,
    );
  }
  return `{ column: "${col}", op: "${op}", value: ${valNode.getText(sf)} }`;
}

/** Rewrite every `ctx.db.query(table, d => …)` in `src` into
 *  `ctx.db.query(table, { filter: <tree> })`. Returns the rewritten source, or the
 *  first translation error (which blocks the save). Non-lambda calls are untouched. */
function rewritePredicateQueries(
  ts: typeof TS,
  src: string,
  sf: TS.SourceFile,
): { code: string; error: LogicDiagnostic | null } {
  const edits: { start: number; end: number; text: string }[] = [];
  let error: LogicDiagnostic | null = null;

  const visit = (node: TS.Node): void => {
    if (error) return;
    if (
      ts.isCallExpression(node) &&
      node.expression.getText(sf) === "ctx.db.query" &&
      node.arguments.length === 2 &&
      ts.isArrowFunction(node.arguments[1])
    ) {
      const arrow = node.arguments[1] as TS.ArrowFunction;
      try {
        const p0 = arrow.parameters[0];
        if (!p0 || !ts.isIdentifier(p0.name)) throw new TranslateError("il predicato deve avere un parametro (es. d => …)", arrow);
        if (ts.isBlock(arrow.body)) throw new TranslateError("il predicato deve essere un'espressione (niente { … })", arrow);
        const tree = translateExpr(ts, arrow.body as TS.Expression, p0.name.text, sf);
        edits.push({ start: arrow.getStart(sf), end: arrow.getEnd(), text: `{ filter: ${tree} }` });
      } catch (err) {
        const te = err as TranslateError;
        const line = te.node
          ? sf.getLineAndCharacterOfPosition(te.node.getStart(sf)).line + 1
          : null;
        error = { message: `Filtro: ${te.message}`, line };
      }
      return; // don't descend into a query we already handled
    }
    ts.forEachChild(node, visit);
  };
  visit(sf);

  if (error) return { code: src, error };
  // Apply right-to-left so earlier offsets stay valid.
  let out = src;
  for (const ed of edits.sort((a, b) => b.start - a.start)) {
    out = out.slice(0, ed.start) + ed.text + out.slice(ed.end);
  }
  return { code: out, error: null };
}

// -- public API --------------------------------------------------------------

/** Transpile a TS logic/compute body → JS + report SYNTACTIC diagnostics AND extract
 *  the declared `Input`/`Output` contract. Pure of the runtime — QuickJS runs `codeJs`
 *  exactly as an untyped body. A plain-JS body transpiles to itself (back-compat). */
export async function compileAppLogic(src: string): Promise<LogicCompileResult> {
  const ts = await loadTs();
  // The ORIGINAL source drives the contract + handler + predicate translation (the
  // author's typed code); a rewritten copy (predicate lambdas → structured `filter`)
  // is what QuickJS actually runs.
  const sf = ts.createSourceFile("logic.ts", src, ts.ScriptTarget.ES2020, true);
  const decls = collectDeclarations(ts, sf);
  const handler = findHandler(ts, sf);

  // ADR-0055 S1b: rewrite `ctx.db.query(t, d => …)` → `ctx.db.query(t, { filter: … })`.
  // A non-translatable predicate is a save-time error (blocks persist), like a syntax error.
  const rewrite = rewritePredicateQueries(ts, src, sf);
  if (rewrite.error) {
    return { ok: false, codeJs: "", sourceMap: null, diagnostics: [rewrite.error], contract: extractContract(ts, decls, handler), hasHandler: handler !== null };
  }

  const emit = ts.transpileModule(rewrite.code, {
    compilerOptions: {
      target: ts.ScriptTarget.ES2020,
      module: ts.ModuleKind.None,
      removeComments: false,
      // Emit a self-contained source map (TS→JS) so the debugger can step the TS
      // source while QuickJS runs the JS. `inlineSources` embeds the exact TS the map
      // was built from (the predicate-rewritten copy) → the map + its source can never
      // drift from the emitted JS. NOT `inlineSourceMap` — we want it as a separate
      // artifact (`emit.sourceMapText`), persisted in `spec.code_map`.
      sourceMap: true,
      inlineSources: true,
    },
    reportDiagnostics: true,
    fileName: "logic.ts",
  });
  const diagnostics: LogicDiagnostic[] = (emit.diagnostics ?? []).map((d) => {
    let line: number | null = null;
    if (d.file && typeof d.start === "number") {
      line = d.file.getLineAndCharacterOfPosition(d.start).line + 1;
    }
    return { message: ts.flattenDiagnosticMessageText(d.messageText, "\n"), line };
  });
  // With `sourceMap:true`, transpileModule appends a `//# sourceMappingURL=…` comment to
  // outputText — strip it so `codeJs` is byte-identical to the pre-source-map behaviour
  // (keeps the runtime JS clean AND lets the debugger's `recompiled === saved` guard match
  // queries saved before source maps). The map (`sourceMapText`) is a separate artifact,
  // and its generated-line coords still align (the stripped comment was the trailing line).
  const jsOut = emit.outputText.replace(/\s*\/\/# sourceMappingURL=\S*\s*$/, "");
  // A `handler(ctx, input)` body defines the function; bridge it to the raw-body runtime
  // by CALLING it (the runtime evals `(function(ctx){ <codeJs> })(ctx)` — see logic_resource).
  const codeJs = handler
    ? `${jsOut.replace(/\s+$/, "")}\nreturn handler(ctx, ctx.input);\n`
    : jsOut;

  return {
    ok: diagnostics.length === 0,
    codeJs,
    sourceMap: emit.sourceMapText ?? null,
    diagnostics,
    contract: extractContract(ts, decls, handler),
    hasHandler: handler !== null,
  };
}

// -- custom TOOL compiler (ADR-0033 Phase D / S3) ----------------------------
//
// A custom TOOL is authored the same way as a Query — a `function handler(input)` —
// but it runs on the sandbox's NODE image (not QuickJS), takes ONE `input` param (no
// `ctx`), and its INPUT SCHEMA is a JSON Schema (with `x-tray-file` for binary params),
// NOT a `QueryParam[]`. So it needs its OWN compile entry (reusing the TS helpers).

/** A property typed `Uint8Array` / `Buffer` / `TrayFile` is a FILE input (the console
 *  uploads it; the sidecar reads it from disk as a Buffer) — the TS twin of Python's
 *  `bytes`. */
function toolMemberSchema(
  ts: typeof TS,
  node: TS.TypeNode | undefined,
  decls: Map<string, TS.TypeNode | TS.InterfaceDeclaration>,
): Record<string, unknown> {
  if (node && ts.isTypeReferenceNode(node)) {
    const name = ts.isIdentifier(node.typeName) ? node.typeName.text : node.typeName.getText();
    if (name === "Uint8Array" || name === "Buffer" || name === "TrayFile") {
      return { type: "string", "x-tray-file": true };
    }
  }
  return typeNodeToSchema(ts, node, decls, new Set()) as Record<string, unknown>;
}

/** The object type of `handler`'s single `input` param → its members, resolving a
 *  reference to a locally-declared `interface Input` / type. */
function toolInputMembers(
  ts: typeof TS,
  node: TS.TypeNode | undefined,
  decls: Map<string, TS.TypeNode | TS.InterfaceDeclaration>,
): TS.NodeArray<TS.TypeElement> | null {
  if (!node) return null;
  if (ts.isTypeLiteralNode(node)) return node.members;
  if (ts.isTypeReferenceNode(node)) {
    const name = ts.isIdentifier(node.typeName) ? node.typeName.text : node.typeName.getText();
    const target = decls.get(name);
    if (target && ts.isInterfaceDeclaration(target)) return target.members;
    if (target && ts.isTypeLiteralNode(target as TS.TypeNode)) {
      return (target as TS.TypeLiteralNode).members;
    }
  }
  return null;
}

/** Build the tool's JSON Schema from `handler`'s `input` param type: each property → a
 *  schema fragment (a `Uint8Array`/`Buffer` prop → `x-tray-file`); a property WITHOUT a
 *  `?` is required. */
function toolInputSchema(
  ts: typeof TS,
  handler: TS.FunctionDeclaration | TS.FunctionExpression | TS.ArrowFunction,
  decls: Map<string, TS.TypeNode | TS.InterfaceDeclaration>,
): Record<string, unknown> {
  const members = toolInputMembers(ts, handler.parameters[0]?.type, decls);
  if (!members) return { type: "object", properties: {} };
  const properties: Record<string, unknown> = {};
  const required: string[] = [];
  for (const m of members) {
    if (!ts.isPropertySignature(m) || !m.name) continue;
    const name =
      ts.isIdentifier(m.name) || ts.isStringLiteral(m.name) ? m.name.text : m.name.getText();
    properties[name] = toolMemberSchema(ts, m.type, decls);
    if (!m.questionToken) required.push(name);
  }
  const out: Record<string, unknown> = { type: "object", properties };
  if (required.length) out.required = required;
  return out;
}

/** The JSDoc comment on `handler` → the tool's model-facing description (the TS twin of
 *  Python's docstring). */
function handlerDescription(
  ts: typeof TS,
  handler: TS.FunctionDeclaration | TS.FunctionExpression | TS.ArrowFunction,
): string | null {
  for (const d of ts.getJSDocCommentsAndTags(handler)) {
    const comment = (d as TS.JSDoc).comment;
    if (typeof comment === "string" && comment.trim()) return comment.trim();
    if (Array.isArray(comment)) {
      const text = comment
        .map((p) => (p as TS.JSDocText).text ?? "")
        .join("")
        .trim();
      if (text) return text;
    }
  }
  return null;
}

export interface ToolCompileResult {
  /** No SYNTACTIC error AND a `handler` was found — safe to persist. */
  ok: boolean;
  /** The transpiled JS the sidecar's node image runs — the `handler` function DEFINED
   *  (Tray's wrapper calls it with the loaded args). NO `return handler(...)` tail. */
  codeJs: string;
  /** JSON Schema derived from the `handler`'s `input` param type (`x-tray-file` for a
   *  `Uint8Array`/`Buffer` prop). */
  inputSchema: Record<string, unknown>;
  /** The `handler` JSDoc → the model-facing description, or null. */
  description: string | null;
  diagnostics: LogicDiagnostic[];
  hasHandler: boolean;
}

/** Compile a custom TOOL's TypeScript → JS + derive its JSON Schema input contract +
 *  description. The runtime is the sandbox node image (NOT QuickJS), so this transpiles
 *  a `function handler(input): Output` body and Tray's `_wrap_javascript` calls it. Pure
 *  of the runtime; a missing `handler` is a save-blocking diagnostic. */
export async function compileToolTs(src: string): Promise<ToolCompileResult> {
  const ts = await loadTs();
  const sf = ts.createSourceFile("tool.ts", src, ts.ScriptTarget.ES2020, true);
  const decls = collectDeclarations(ts, sf);
  const handler = findHandler(ts, sf);

  const emit = ts.transpileModule(src, {
    compilerOptions: {
      target: ts.ScriptTarget.ES2020,
      module: ts.ModuleKind.None,
      removeComments: false,
    },
    reportDiagnostics: true,
    fileName: "tool.ts",
  });
  const diagnostics: LogicDiagnostic[] = (emit.diagnostics ?? []).map((d) => {
    let line: number | null = null;
    if (d.file && typeof d.start === "number") {
      line = d.file.getLineAndCharacterOfPosition(d.start).line + 1;
    }
    return { message: ts.flattenDiagnosticMessageText(d.messageText, "\n"), line };
  });
  if (!handler) {
    diagnostics.push({
      message:
        "Manca la funzione handler(input). Definisci: function handler(input: Input): Output { … }",
      line: null,
    });
  }
  const codeJs = emit.outputText.replace(/\s*\/\/# sourceMappingURL=\S*\s*$/, "");
  return {
    ok: diagnostics.length === 0 && handler !== null,
    codeJs,
    inputSchema: handler ? toolInputSchema(ts, handler, decls) : { type: "object", properties: {} },
    description: handler ? handlerDescription(ts, handler) : null,
    diagnostics,
    hasHandler: handler !== null,
  };
}
